import type { ImageViewerRolodexOverride } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageViewerLogic'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useViewerCantileverPageImageRef } from 'features/knowledgeDocumentViewer/hooks/useViewerCantileverPageImageRef'
import {
  resolvePageImageRolodexSettleState,
  resolvePdfUrlForPageImageFallback,
} from 'features/knowledgeDocumentViewer/utils/pageImageRolodexSettle'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'

type UseCurrentPageRolodexOverrideResult = {
  currentPageUrl: string | undefined
  pdfUrlForFallback: string | undefined
  currentPageOverride: ImageViewerRolodexOverride | undefined
}

/**
 * Current-page Rolodex hi-res settle for continuous overlay + PDF gating.
 *
 * Withholds the PDF signed URL until the 6400px lookup fails so deep-links
 * cannot start PDF.js while page images are still in flight (POWER-117).
 * A hi-res image the page slot is not showing, because it gave up on it or
 * is withholding it while a manual retry re-signs it, counts as a failed
 * lookup.
 */
export function useCurrentPageRolodexOverride(
  currentPage: number,
  pdfUrl: string,
  rolodexHidden: boolean
): UseCurrentPageRolodexOverrideResult {
  const { isUuid5PageImageLookupUnavailable } = useDocumentTabDataContext()
  const currentPageRolodexQuery = useViewerCantileverPageImageRef(
    currentPage,
    'hiRes'
  )
  const { url: signedUrl, isLoading: isCurrentPageUrlLoading } =
    useRolodexSignedURLs(currentPageRolodexQuery)
  const currentPageUrl = rolodexHidden ? undefined : signedUrl
  const currentPageHiResSettle = resolvePageImageRolodexSettleState({
    hasQuery: currentPageRolodexQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url: currentPageUrl,
    isLoading: isCurrentPageUrlLoading,
  })
  const pdfUrlForFallback = resolvePdfUrlForPageImageFallback(
    currentPageHiResSettle,
    pdfUrl || undefined
  )
  const currentPageOverride = useMemo<ImageViewerRolodexOverride | undefined>(
    () =>
      currentPageUrl
        ? { imageUrl: currentPageUrl, isLoading: false }
        : currentPageHiResSettle.isPending
          ? { imageUrl: undefined, isLoading: true }
          : undefined,
    [currentPageUrl, currentPageHiResSettle.isPending]
  )

  return { currentPageUrl, pdfUrlForFallback, currentPageOverride }
}
